import React, { useCallback, useEffect, useState } from 'react';
import { fetchStores, createStore, updateStore } from '../../api/stores';

const emptyForm = { name: '', address: '', active: true };

export default function StoresManager({ authConfig, onUnauthorized, onStoresChange }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchStores();
      setStores(Array.isArray(list) ? list : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || 'Failed to load stores.');
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEdit = (store) => {
    setEditingId(store._id || store.id);
    setShowAddModal(false);
    setForm({
      name: store.name || '',
      address: store.address || '',
      active: store.active !== false
    });
    setError('');
    setMessage('');
  };

  const resetForm = () => {
    setEditingId(null);
    setShowAddModal(false);
    setForm(emptyForm);
    setError('');
    setMessage('');
  };

  const openAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.name.trim()) {
      setError('Store name is required.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        name: form.name.trim(),
        address: (form.address || '').trim(),
        active: form.active
      };
      if (editingId) {
        await updateStore(editingId, payload, authConfig);
        setMessage('Store updated.');
      } else {
        await createStore(payload, authConfig);
        setMessage('Store added.');
      }
      resetForm();
      await load();
      if (onStoresChange) onStoresChange();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.error || 'Failed to save store.');
    } finally {
      setSaving(false);
    }
  };

  const renderForm = ({ submitLabel }) => (
    <form onSubmit={handleSubmit} className="space-y-3 p-5">
      <input
        type="text"
        name="name"
        placeholder="Store name (e.g. iConnect Home Wireless Network & Data Solution - Main (TAGUM))"
        className="border w-full p-2 rounded text-sm"
        value={form.name}
        onChange={handleChange}
        required
      />
      <textarea
        name="address"
        placeholder="Address"
        className="border w-full p-2 rounded text-sm"
        value={form.address}
        onChange={handleChange}
        rows={2}
      />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
        Active
      </label>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-60 hover:bg-indigo-500">
          {saving ? 'Saving...' : submitLabel}
        </button>
        <button type="button" onClick={resetForm} className="flex-1 px-4 py-2 border rounded text-sm text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  );

  if (loading) return <p className="text-gray-600">Loading stores...</p>;

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-green-700">{message}</p>}

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-medium text-gray-800">Stores</h4>
            <p className="text-xs text-gray-500">Manage branch/store locations.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={openAdd} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"><span className="text-lg leading-none">+</span>Add</button>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
              <button type="button" onClick={() => setViewMode('list')} className={`rounded-md px-3 py-1.5 ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>List</button>
              <button type="button" onClick={() => setViewMode('grid')} className={`rounded-md px-3 py-1.5 ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Grid</button>
            </div>
          </div>
        </div>
        {stores.length === 0 ? (
          <p className="text-gray-500 text-sm">No stores yet.</p>
        ) : viewMode === 'grid' ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {stores.map((store) => (
              <div key={store._id || store.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="truncate font-semibold text-gray-900">{store.name || 'Unnamed store'}</p>
                <p className="mt-1 line-clamp-2 text-sm text-gray-500">{store.address || 'No address'}</p>
                <span className={`mt-3 inline-flex rounded-full px-2 py-0.5 text-xs ${store.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {store.active !== false ? 'Active' : 'Inactive'}
                </span>
                <div className="mt-4">
                  <button type="button" onClick={() => handleEdit(store)} className="w-full text-sm border px-3 py-2 rounded hover:bg-gray-50">Edit</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {stores.map((store) => (
              <li
                key={store._id || store.id}
                className="flex items-center justify-between p-3 border rounded bg-white"
              >
                <div>
                  <p className="font-medium">{store.name || 'Unnamed store'}</p>
                  {store.address && (
                    <p className="text-sm text-gray-500">{store.address}</p>
                  )}
                  <span
                    className={`text-xs ${store.active !== false ? 'text-green-600' : 'text-gray-400'}`}
                  >
                    {store.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleEdit(store)}
                  className="border px-3 py-2 rounded text-sm hover:bg-gray-50"
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddModal && !editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="add-store-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="add-store-title" className="font-semibold text-gray-900">Add store</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close add store">×</button>
            </div>
            {renderForm({ submitLabel: 'Add store' })}
          </div>
        </div>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="edit-store-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="edit-store-title" className="font-semibold text-gray-900">Edit store</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close edit store">×</button>
            </div>
            {renderForm({ submitLabel: 'Update store' })}
          </div>
        </div>
      )}
    </div>
  );
}
